import { computed, ref, unref, watch } from 'vue';
import { normalizePostSaleSection } from './postSaleInitialSection';

const routeString = value => (Array.isArray(value) ? value[0] : value);
const routeArray = value => {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
};

const filtersFromQuery = query => ({
  q: routeString(query.q) || '',
  responsible_user_id: routeString(query.responsible_user_id) || '',
  priority: routeString(query.priority) || '',
  deadline: routeString(query.deadline) || '',
  payment_method: routeString(query.payment_method) || '',
  laudemio_type: routeString(query.laudemio_type) || '',
  clients: routeArray(query.clients),
  address_street: routeString(query.address_street) || '',
  address_number: routeString(query.address_number) || '',
  address_unit: routeString(query.address_unit) || '',
});

export function usePostSaleRouteState({
  route,
  router,
  initialSection = 'gestao',
}) {
  const defaultSection = () =>
    normalizePostSaleSection(unref(initialSection), 'gestao');
  const activeSection = ref(defaultSection());
  const activeList = ref('andamento');
  const managementTab = ref('graficos');
  const filters = ref(filtersFromQuery(route.query));
  const page = ref(1);
  const sort = ref(routeString(route.query.sort) || '');

  const routePostSaleCaseId = computed(
    () => route.params.postSaleCaseId?.toString() || ''
  );
  const queryState = () => ({
    section: activeSection.value === 'gestao' ? undefined : activeSection.value,
    list: activeList.value === 'andamento' ? undefined : activeList.value,
    gestao:
      managementTab.value === 'graficos' ? undefined : managementTab.value,
    page: page.value > 1 ? String(page.value) : undefined,
    sort: sort.value || undefined,
    q: filters.value.q || undefined,
    responsible_user_id: filters.value.responsible_user_id || undefined,
    priority: filters.value.priority || undefined,
    deadline: filters.value.deadline || undefined,
    payment_method: filters.value.payment_method || undefined,
    laudemio_type: filters.value.laudemio_type || undefined,
    clients: filters.value.clients?.length ? filters.value.clients : undefined,
    address_street: filters.value.address_street || undefined,
    address_number: filters.value.address_number || undefined,
    address_unit: filters.value.address_unit || undefined,
  });

  const indexLocation = () => ({
    name: 'operacao_imobiliaria_pos_venda',
    params: { accountId: route.params.accountId },
    query: queryState(),
  });

  const caseLocation = (postSaleCaseId, name) => ({
    name,
    params: { accountId: route.params.accountId, postSaleCaseId },
    query: queryState(),
  });

  const syncIndex = () => router.replace(indexLocation());

  const openCase = postSaleCase =>
    router.push(
      caseLocation(postSaleCase.id, 'operacao_imobiliaria_pos_venda_detalhes')
    );

  const closeCase = () => router.push(indexLocation());

  const openTransition = postSaleCaseId =>
    router.push(
      caseLocation(postSaleCaseId, 'operacao_imobiliaria_pos_venda_status')
    );

  const syncFromRoute = () => {
    activeSection.value = normalizePostSaleSection(
      routeString(route.query.section),
      defaultSection()
    );
    activeList.value = routeString(route.query.list) || 'andamento';
    managementTab.value = routeString(route.query.gestao) || 'graficos';
    page.value = Number(routeString(route.query.page) || 1);
    sort.value = routeString(route.query.sort) || '';
    filters.value = filtersFromQuery(route.query);
  };

  watch(() => route.fullPath, syncFromRoute, { immediate: true });

  return {
    activeList,
    activeSection,
    closeCase,
    filters,
    indexLocation,
    managementTab,
    openCase,
    openTransition,
    page,
    sort,
    queryState,
    routePostSaleCaseId,
    syncIndex,
  };
}
